/** @jsx jsx */

import React, { useState } from 'react';
import { jsx, css } from '@emotion/core';

import { GlobalStyles, Toast, AlertVariant } from 'react-magma-dom';
import { FacultyProfileUserData } from '../FacultyProfile/FacultyProfileData';
import { UserDataKeys } from '../Profile/UserDataKeys';
import { FacultyDataKeys } from '../Profile/FacultyDataKeys';
import { MarketOptInValues } from '../Profile/MarketOptIn';
import { updateProfile } from '../../../api';
import { FacultyProfileDataBoundary } from '../../boundaries/FacultyProfileDataBoundary';
import { Router } from '@reach/router';
import { ErrorMessage } from '../../basic/ErrorMessage';
import { buildPathTo } from '../../../navigation/path-builder';
import { NotFound } from '../../router-wrap/NotFound';
import { Moved } from '../../router-wrap/Moved';
import { AlfredGlobalStyles } from '../../basic/AlfredGlobalStyles';
import { ErrorBoundaryDefault } from '../../error/ErrorBoundaryDefault';
import { ToastProps } from 'react-magma-dom/dist/components/Toast';
import { trackingClient } from '../../../ext/tracking';
import { Main } from '../../a11y/landmark/Main';

const SAMPLE_DATA: FacultyProfileUserData = {
  [UserDataKeys.accountName]: 'Sample User',
  [UserDataKeys.login]: 'sample.user@fake.cengage.com',
  [UserDataKeys.email]: 'sample.user@fake.cengage.com',
  [UserDataKeys.alternateLogin]: '',
  [UserDataKeys.phoneNumber]: '555-555-5555',
  [UserDataKeys.firstName]: 'Sample',
  [UserDataKeys.lastName]: 'User',
  [UserDataKeys.preferredName]: 'Sam',
  [UserDataKeys.birthYear]: '1950',
  [UserDataKeys.marketOptIn]: 'Y' as MarketOptInValues,
  [UserDataKeys.passwordLastChanged]: '7/7/2019', //NOSONAR
  [UserDataKeys.timezone]: '(GMT -8:00) Pacific time (US & Canada)',
  [UserDataKeys.instId]: '4265',
  [UserDataKeys.instName]: 'STANFORD'
};

const backendAndFrontendFields = [
  UserDataKeys.login,
  UserDataKeys.email,
  UserDataKeys.alternateLogin,
  UserDataKeys.phoneNumber,
  UserDataKeys.firstName,
  UserDataKeys.lastName,
  UserDataKeys.preferredName,
  UserDataKeys.birthYear,
  UserDataKeys.passwordLastChanged,
  UserDataKeys.timezone,
  UserDataKeys.instId,
  UserDataKeys.instName,
  FacultyDataKeys.entityName,
  FacultyDataKeys.entityCity,
  FacultyDataKeys.entityState,
  FacultyDataKeys.entityCountry,
  FacultyDataKeys.entityRegion,
  FacultyDataKeys.supervisorName,
  FacultyDataKeys.supervisorEmail,
  FacultyDataKeys.supervisorPhone
];

const copyBackendFrontend = (src: any, dest: any = {}) => {
  for (const field of backendAndFrontendFields) {
    if (undefined !== src[field] && null !== src[field]) {
      dest[field] = src[field];
    }
  }
  return dest;
};

export const loadDataFromBackend = (loadFrom: any) => {
  if (loadFrom?.backend?.userData) {
    const userData = loadFrom.backend.userData;

    // Since optOut is provided, but optIn is how it is presented here
    // Might be good to consider switching it, but not sure,
    // not very elegant either way...
    const invertMarket = (yesOrNo: string): MarketOptInValues => {
      if (!yesOrNo) {
        return MarketOptInValues.n;
      }
      if ('Y' === yesOrNo.toUpperCase()) {
        return MarketOptInValues.n;
      }
      return MarketOptInValues.y;
    };

    const buildAccountName = ({
      firstName,
      lastName
    }: {
      firstName: string;
      lastName: string;
    }) => {
      return `${firstName} ${lastName}`;
    };

    // NOTE, Some mismatches below with format: [provided] expected
    // [userName] username
    // [marketingOptOut] marketingOptIn
    const data = copyBackendFrontend(userData);
    const result: FacultyProfileUserData = {
      [UserDataKeys.accountName]: buildAccountName(userData),
      [UserDataKeys.login]: userData.userName,
      [UserDataKeys.alternateLogin]: userData.alternateEmail,
      [UserDataKeys.marketOptIn]: invertMarket(userData.marketingOptOut),
      ...data
    };

    return result;
  }

  return SAMPLE_DATA;
};

export const buildForBackend = (data: any) => {
  // NOTE, Some mismatches below with format: [provided] expected
  // [marketingOptIn] marketingOptOut
  const result = copyBackendFrontend(data);
  if (data[UserDataKeys.marketOptIn]) {
    const value = data[UserDataKeys.marketOptIn].toUpperCase();
    if ('Y' === value) {
      result.marketingOptOut = 'N';
    } else if ('N' === value) {
      result.marketingOptOut = 'Y';
    }
  }

  return result;
};

interface FacultyProfileProps {
  userData?: FacultyProfileUserData;
}

export const FacultyProfile: React.FunctionComponent<FacultyProfileProps> = (
  props: FacultyProfileProps
) => {
  const { userData } = props;

  // Data that will be shown to the user; modifying this will refresh
  const [dataToShow, setDataToShow] = useState(
    userData || loadDataFromBackend(window)
  );

  const [hasError, setHasError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [toastMessage, setToastMessage] = useState(
    null as null | Omit<ToastProps, 'onDismiss'>
  );

  return (
    <React.Fragment>
      {toastMessage && (
        <Toast {...toastMessage} onDismiss={setToastMessage.bind(null, null)} />
      )}
      <GlobalStyles />
      <AlfredGlobalStyles />
      <Main>
        <div
          css={css`
            // Center this and make it not terribly wide
            margin: 0 auto;
            max-width: 60vmax;
          `}
        >
          <ErrorBoundaryDefault>
            <ErrorMessage
              errorMessage={errorMessage}
              onDismiss={setHasError.bind(null, false)}
              isShown={hasError}
            />
            <Router basepath={buildPathTo.onlyPath.facultyProfile.root()}>
              <NotFound
                basePath={buildPathTo.facultyProfile.root()}
                label="the Profile page"
              />
              <Moved
                path="/"
                movedTo={buildPathTo.facultyProfile.profileData({})}
              />

              <FacultyProfileDataBoundary
                onDataSave={async dataToSave => {
                  try {
                    await updateProfile(buildForBackend(dataToSave));

                    // Currently the only view that is supported is profileEdit so the category
                    // doesn't need to be customizable yet
                    for (const key of Object.keys(dataToSave)) {
                      trackingClient.event.profileEdit.changeData({
                        tags: [{ key: 'data-name', value: key }]
                      });
                    }

                    setToastMessage({
                      children: <React.Fragment>Success!</React.Fragment>,
                      variant: AlertVariant.success
                    });
                    setHasError(false);
                    setDataToShow({
                      ...dataToShow,
                      ...dataToSave
                    });
                    return true;
                  } catch (error: any) {
                    setHasError(true);
                    setErrorMessage(
                      'There was an error when saving the profile:\n' +
                        error.message
                    );
                    return false;
                  }
                }}
                userData={dataToShow}
              />
            </Router>
          </ErrorBoundaryDefault>
        </div>
      </Main>
    </React.Fragment>
  );
};
